/**
 * FloatingCookieButton - World-class persistent cookie access
 * 
 * Benchmarked against: OneTrust, Cookiebot, Termly
 * - Appears only AFTER user has consented (not before)
 * - Small, unobtrusive floating button
 * - Quick access to change preferences
 * - Accessible and keyboard navigable
 */

import { useState } from 'react';
import { Cookie } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { CookiePreferencesDialog } from './CookiePreferencesDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function FloatingCookieButton() {
  const { hasConsented } = useCookieConsent();
  const [showDialog, setShowDialog] = useState(false);

  // Only show after user has made a consent choice
  if (!hasConsented) return null;

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                type: 'spring', 
                stiffness: 260, 
                damping: 20,
                delay: 1 
              }}
              className="fixed bottom-4 left-4 z-40"
            >
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowDialog(true)}
                className="h-10 w-10 rounded-full shadow-lg bg-card/95 backdrop-blur-sm border-border/50 hover:border-primary/50 hover:shadow-xl transition-all duration-300"
                aria-label="Cookie preferences"
              >
                <Cookie className="h-4 w-4 text-muted-foreground" />
              </Button>
            </motion.div>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">
            Cookie Preferences
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <CookiePreferencesDialog 
        open={showDialog} 
        onOpenChange={setShowDialog} 
      />
    </>
  );
}
